import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { ArrowLeft, RefreshCw, AlertTriangle, Clock, Phone, Calendar, User } from 'lucide-react';

const OverdueFollowups = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [overdue, setOverdue] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOverdue = async () => {
    setLoading(true);
    try {
      const res = await api.get('/crm/overdue-followups');
      setOverdue(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Refetch every time this page is navigated to (including browser back)
  useEffect(() => { fetchOverdue(); }, [location.key]);

  // Group by salesman
  const grouped = {};
  overdue.forEach(lead => {
    const key = lead.follow_up_salesman_name || lead.assigned_salesman_name || 'Unassigned';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(lead);
  });

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="overdue-followups-page">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-300" /> Overdue Follow-ups
              </h1>
              <p className="text-xs text-emerald-200">{overdue.length} overdue leads requiring action</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={fetchOverdue}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Summary Bar */}
        {overdue.length > 0 && (
          <div className="px-4 pt-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3" data-testid="overdue-summary">
              <AlertTriangle className="w-8 h-8 text-red-500 flex-shrink-0" />
              <div>
                <p className="font-bold text-red-800">{overdue.length} follow-up{overdue.length > 1 ? 's' : ''} overdue</p>
                <p className="text-sm text-red-600">
                  {overdue.filter(l => l.days_overdue > 7).length} are over a week late.
                  These leads need immediate attention to prevent pipeline loss.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Grouped by Salesman */}
        <div className="px-4 pt-3 pb-4 space-y-4" data-testid="overdue-list">
          {loading ? (
            <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-emerald-600" /></div>
          ) : overdue.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Clock className="w-8 h-8 text-green-500" />
              </div>
              <p className="font-semibold text-gray-700">All caught up!</p>
              <p className="text-sm text-gray-400 mt-1">No overdue follow-ups. Great job!</p>
            </div>
          ) : (
            Object.entries(grouped).map(([salesman, leads]) => (
              <div key={salesman} className="bg-white rounded-lg border overflow-hidden">
                {/* Salesman header */}
                <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-500" />
                    <span className="font-semibold text-sm text-gray-800">{salesman}</span>
                  </div>
                  <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">{leads.length} overdue</span>
                </div>

                {/* Leads */}
                <div className="divide-y">
                  {leads.sort((a, b) => (b.days_overdue || 0) - (a.days_overdue || 0)).map(lead => (
                    <div
                      key={lead._id}
                      className="px-4 py-3 hover:bg-red-50/50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/lead-sessions/${lead.session_id}`)}
                      data-testid={`overdue-lead-${lead._id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm text-gray-900">{lead.product_name}</span>
                            {lead.main_group && <span className="text-xs text-gray-500">{lead.main_group}</span>}
                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                              lead.days_overdue > 7 ? 'bg-red-500 text-white' :
                              lead.days_overdue > 3 ? 'bg-red-100 text-red-700 border border-red-200' :
                              'bg-amber-100 text-amber-700 border border-amber-200'
                            }`}>
                              {lead.days_overdue} day{lead.days_overdue !== 1 ? 's' : ''} overdue
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500 mt-1 flex-wrap">
                            <span className="font-medium text-gray-700">{lead.client_name}</span>
                            {lead.client_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{lead.client_phone}</span>}
                            <span>{lead.session_number}</span>
                            {lead.occasion && <span>{lead.occasion}</span>}
                          </div>
                          <div className="flex items-center gap-3 text-xs mt-1">
                            <span className="text-red-600 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Follow-up was: {new Date(lead.follow_up_date).toLocaleDateString('en-IN')}
                            </span>
                            {lead.reason && <span className="text-gray-400">Reason: {lead.reason}</span>}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-3">
                          {lead.estimated_value > 0 && (
                            <p className="font-bold text-sm text-emerald-700">₹{Math.round(lead.estimated_value).toLocaleString('en-IN')}</p>
                          )}
                          <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] mt-1 border-red-300 text-red-600 hover:bg-red-50">
                            Take Action
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default OverdueFollowups;
